import type { UseQueryResult } from '@tanstack/react-query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { PetitionUpdatesData, QueryState } from '#src/app/pages/petitionDetails/shared/types';

import { getPetitionUpdates } from './getPetitionUpdates';

export function usePetitionUpdatesQuery(queryState: QueryState): UseQueryResult<PetitionUpdatesData> {
	const {
		data: {
			petition: { id },
		},
	} = usePetitionDetailsPageData();

	const utilityContext = useUtilityContext();

	// `queryState.updates` (the full list of loaded updates) is deliberately not part of the key: it is derived
	// from the cursor/count, and hashing it on every render would grow with each "load more"
	// eslint-disable-next-line @tanstack/query/exhaustive-deps
	return useQuery({
		queryKey: ['petitionDetails/updates', id, queryState.cursor, queryState.count],
		queryFn: async () => getPetitionUpdates(id, queryState, utilityContext),
		// fail fast when offline (like the legacy hook) instead of pausing on a spinner with no error or retry
		networkMode: 'always',
		// keeps the previous page's data while the next one loads, so the list doesn't flash empty
		placeholderData: keepPreviousData,
	});
}
